import { useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import type {
  CosaFareOggiResponse,
  DashboardWidget,
  DecisioniAnomalieResponse,
  ModuloSommario,
  StatoDatiResponse,
} from "@/types/api";

const AUTO_REFRESH_MS = 5 * 60 * 1000;

export function useDashboardWidgets() {
  return useQuery({
    queryKey: ["dashboard", "widgets"],
    queryFn: () => apiFetch<DashboardWidget[]>("/api/v1/dashboard/widgets"),
    staleTime: 60_000,
    refetchInterval: AUTO_REFRESH_MS,
  });
}

export function useStatoDati() {
  return useQuery({
    queryKey: ["dashboard", "stato-dati"],
    queryFn: () => apiFetch<StatoDatiResponse>("/api/v1/dashboard/stato-dati"),
    staleTime: 30_000,
    refetchInterval: AUTO_REFRESH_MS,
  });
}

export function useCosaFareOggi() {
  return useQuery({
    queryKey: ["dashboard", "cosa-fare-oggi"],
    queryFn: () =>
      apiFetch<CosaFareOggiResponse>("/api/v1/dashboard/cosa-fare-oggi"),
    staleTime: 30_000,
    refetchInterval: AUTO_REFRESH_MS,
  });
}

export function useDecisioniAnomalie() {
  return useQuery({
    queryKey: ["dashboard", "decisioni-anomalie"],
    queryFn: () =>
      apiFetch<DecisioniAnomalieResponse>("/api/v1/dashboard/decisioni-anomalie"),
    staleTime: 30_000,
    refetchInterval: AUTO_REFRESH_MS,
  });
}

export function useModuloSommario(codice: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["moduli", "sommario", codice],
    queryFn: () =>
      apiFetch<ModuloSommario>(`/api/v1/moduli/${codice}/sommario`),
    enabled: enabled && !!codice,
    staleTime: 30_000,
    refetchInterval: AUTO_REFRESH_MS,
  });
}

/**
 * Refresh manuale della dashboard. Invalida le query della dashboard E quelle
 * dei dati di modulo che la alimentano (M1/M3, sommari, moduli): il bottone
 * "Aggiorna" deve aggiornare anche i numeri vivi (SPEC-A §8.3 / fix doc 01 §1).
 */
export function useDashboardRefresh() {
  const qc = useQueryClient();
  return () => {
    for (const key of [["dashboard"], ["moduli"], ["m1"], ["m3"]]) {
      qc.invalidateQueries({ queryKey: key });
    }
  };
}

/** I tipi che arrivano dal gestionale (chip «Stato dati» con fonte gestionale):
 *  dal 05-10-2026 ordini e anagrafiche si leggono dal DB di frontiera ogni 15
 *  minuti, e caricarli a mano sarebbe una seconda fonte dello stesso dato. Se il
 *  collegamento è spento i chip tornano «file» e le aree di caricamento
 *  ricompaiono da sole. */
export function useTipiDalGestionale(): Set<string> {
  const { data } = useStatoDati();
  return new Set((data?.files ?? []).filter((f) => f.fonte === "gestionale").map((f) => f.tipo_file));
}
